import React from 'react';

import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { useThemeContext } from '@infoods/lifebase-ui';

import { getValuesForFirstAndLast } from './utils/getValuesForFirstAndLast';
import { drawDashLine } from '../../../../../../../../shared/features/charts';
import { drawLabel } from './utils/drawLabel';
import { getDataLabelColor } from './utils/getDataLabelColor';

ChartJS.register(LineController, LineElement, PointElement, CategoryScale, LinearScale);

type LongListChartProps = {
  labels: string[][];
  values: number[];
};

const LongListChart = ({ labels, values }: LongListChartProps) => {
  const { palette } = useThemeContext();

  const gridColors = getValuesForFirstAndLast(
    values,
    'rgba(0, 0, 0, 0.1)',
    'transparent',
  );
  const ticksColors = getValuesForFirstAndLast(
    values,
    palette.neutral[700],
    'transparent',
  );
  const pointRadius = getValuesForFirstAndLast(values, 3, 0);

  const ticksFont = {
    family: "'Lato', sans-serif",
    size: 12,
    weight: 400,
    lineHeight: 1.2,
  } as const;

  return (
    <Line
      data={{ labels, datasets: [{ data: values, pointRadius }] }}
      plugins={[
        ChartDataLabels,
        {
          id: 'valueOfPoint',
          afterDraw: (chart) => {
            const activeElements = chart.getActiveElements();

            if (activeElements.length > 0) {
              const { ctx, chartArea, data } = chart;
              const { element, index: activeIndex } = activeElements[0];

              const pointX = element.x;
              const pointY = element.y;

              drawDashLine(
                ctx,
                chartArea,
                {
                  pointX,
                  pointY,
                  gapFromStart: 4,
                },
                palette.neutral[400],
              );

              drawLabel(
                ctx,
                chartArea,
                {
                  label: data.labels![activeIndex] as string[],
                  pointX,
                  gapFromChartArea: 18.2,
                  color: palette.neutral[700],
                  font: ticksFont,
                },
                palette,
              );
            }
          },
        },
      ]}
      options={{
        interaction: {
          mode: 'index',
          intersect: false,
        },
        animation: false,
        maintainAspectRatio: false,
        elements: {
          line: {
            borderColor: palette.mainBlack,
            borderWidth: 2,
          },
          point: {
            backgroundColor: palette.mainBlack,
            hoverRadius: 4,
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          datalabels: {
            color: (context) => getDataLabelColor(context, palette),
            font: { weight: 700 },
            clamp: true,
            offset: 10,
            align: 'top',
          },
        },
        scales: {
          x: {
            offset: true,
            grid: {
              color: gridColors,
            },
            border: { dash: [3, 3] },
            ticks: {
              maxRotation: 0,
              autoSkip: false,
              color: ticksColors,
              font: ticksFont,
            },
          },
          y: {
            offset: true,
            display: false,
            grace: 5,
          },
        },
      }}
    />
  );
};

export { LongListChart };
